const normalizeLookupKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/^_hide_/, "")
    .replace(/[\s_-]+/g, "")
    .trim();

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const ALWAYS_HIDDEN_PARAMETER_KEYS = new Set([
  "atypicallymphocyte",
  "hcvabmethodology",
]);

const hiddenParameterSetCache = new WeakMap();

const buildHiddenParameterSet = (masterData) => {
  const hidden = new Set();
  if (!masterData || typeof masterData !== "object") {
    return hidden;
  }

  const cached = hiddenParameterSetCache.get(masterData);
  if (cached) {
    return cached;
  }

  for (const categoryData of Object.values(masterData)) {
    if (!categoryData || typeof categoryData !== "object") {
      continue;
    }

    for (const key of Object.keys(categoryData)) {
      if (key.startsWith("_hide_")) {
        hidden.add(normalizeLookupKey(key));
      }
    }
  }

  hiddenParameterSetCache.set(masterData, hidden);

  return hidden;
};

const shouldHideParameterByMasterData = (param, masterData) => {
  const normalizedName = normalizeLookupKey(param?.name);
  if (ALWAYS_HIDDEN_PARAMETER_KEYS.has(normalizedName)) {
    return true;
  }

  const hiddenNames = buildHiddenParameterSet(masterData);
  if (hiddenNames.size === 0) {
    return false;
  }

  if (!hiddenNames.has(normalizedName)) {
    return false;
  }

  const value = normalizeText(param?.result);
  const normal = normalizeText(param?.normal);
  return value !== "" && value === normal;
};

const normalizeParameterItem = (item) => {
  if (!item) return null;

  if (typeof item !== "object" || Array.isArray(item)) {
    if (item === null || item === undefined || item === "") {
      return null;
    }

    return {
      name: String(item),
      result: String(item),
      normal: "-",
      unit: "",
    };
  }

  const name = String(
    item.name || item.test_name || item.parameter_name || item.key || "",
  ).trim();
  const result =
    item.result ?? item.value ?? item.data ?? item.normal ?? item.normal_range;
  if (!name || result === null || result === undefined || result === "") {
    return null;
  }

  return {
    ...item,
    name,
    result: String(result),
    value: String(result),
    normal: String(item.normal ?? item.normal_range ?? "-"),
    normal_range: String(item.normal_range ?? item.normal ?? "-"),
    unit: String(item.unit ?? ""),
  };
};

const normalizeParameterCollection = (source) => {
  if (!Array.isArray(source)) {
    return [];
  }

  const items = [];
  for (const item of source) {
    const normalized = normalizeParameterItem(item);
    if (normalized) {
      items.push(normalized);
    }
  }
  return items;
};

const getCandidateParameterSources = (record) => {
  const sources = [];
  const push = (source) => {
    if (Array.isArray(source) && source.length > 0) {
      sources.push(source);
    }
  };

  const addDerivedFromLabData = (labData) => {
    if (!labData || typeof labData !== "object" || Array.isArray(labData)) {
      return;
    }

    const derived = [];
    const add = (name, result, normal = "-", unit = "") => {
      if (result === null || result === undefined || result === "") {
        return;
      }

      derived.push({
        name,
        result: String(result),
        value: String(result),
        normal: String(normal ?? "-"),
        normal_range: String(normal ?? "-"),
        unit: String(unit ?? ""),
      });
    };

    const stoolColor =
      labData.stool_color_result || labData.directsmearstoolcolorresult;
    const stoolRbc =
      labData.stool_rbc_result || labData.directsmearstoolrbcresult;
    const stoolWbc =
      labData.stool_wbc_result || labData.directsmearstoolwbcresult;
    const hemoglobinStool =
      labData.hemoglobinbdetection ||
      labData.hemoglobindetectionstoolresult ||
      labData.hemoglobin_detection_stool_result ||
      labData.hemoglobinb_detection_stool_hemoglobin_detection_stool_result;
    const stoolParasite = labData.stool_parasite_result || labData.parasite;
    const stoolConsistency =
      labData.stool_consistency_result ||
      labData.consistency ||
      labData.directsmearstoolconsistencyresult;

    add("Stool Color", stoolColor, labData.stool_colornormal || "-", "");
    add("Stool RBC", stoolRbc, labData.stool_rbcnormal || "-", "");
    add("Stool WBC", stoolWbc, labData.stool_wbcnormal || "-", "");
    add(
      "Hemoglobin detection stool",
      hemoglobinStool,
      labData.hemoglobin_detection_stool_normal_result ||
        labData.hemoglobindetectionstoolnormalvalue ||
        "-",
      "",
    );
    add("Parasite", stoolParasite, labData.stool_parasitenormal || "-", "");
    add(
      "Consistency",
      stoolConsistency,
      labData.stool_consistencynormal || "-",
      "",
    );

    if (derived.length > 0) {
      sources.push(derived);
    }
  };

  const addUrineDerivedFromLabData = (labData) => {
    if (!labData || typeof labData !== "object" || Array.isArray(labData)) {
      return;
    }

    const derived = [];
    const add = (name, result, normal = "-", unit = "") => {
      if (result === null || result === undefined || result === "") {
        return;
      }

      derived.push({
        name,
        result: String(result),
        value: String(result),
        normal: String(normal ?? "-"),
        normal_range: String(normal ?? "-"),
        unit: String(unit ?? ""),
      });
    };

    add(
      "Urinalysis Others",
      labData.urinalysisothersresult ||
        labData.urinalysisothers ||
        labData.urinalysis_others_result,
      labData.urinalysisothersnormalvalue ||
        labData.urinalysisothersnormal ||
        "-",
      "",
    );
    add(
      "Mucus thread",
      labData.mucusthreadresult ||
        labData.urinalysismucusthreadresult ||
        labData.mucusthread ||
        labData.urinalysismucusthread,
      labData.mucusthreadnormalvalue ||
        labData.urinalysismucusthreadnormalvalue ||
        "-",
      "",
    );
    add(
      "Calcium Oxalate",
      labData.calciumoxalateresult ||
        labData.urinalysiscalciumoxalateresult ||
        labData.urinalysiscalciumoxalate,
      labData.calciumoxalatenormalvalue ||
        labData.urinalysiscalciumoxalatenormalvalue ||
        "-",
      "",
    );
    add(
      "Calcium Carbonate crystal",
      labData.calciumcarbonatecrystalresult ||
        labData.calciumcarbonateresult ||
        labData.urinalysiscalciumcarbonatecrystalresult ||
        labData.urinalysiscalciumcarbonateresult ||
        labData.urinalysiscalciumcar ||
        labData.urinalysiscalciumcarbonate,
      labData.calciumcarbonatecrystalnormalvalue ||
        labData.calciumcarbonatenormalvalue ||
        labData.urinalysiscalciumcarbonatecrystalnormalvalue ||
        labData.urinalysiscalciumcarbonatenormalvalue ||
        "-",
      "",
    );
    add(
      "Uric Acid Crystal",
      labData.urinalysisuricacidcrystalresult ||
        labData.urinalysisuricacidcrystal ||
        labData.uricacidcrystalresult,
      labData.urinalysisuricacidcrystalnormalvalue ||
        labData.urinalysisuricacidcrystalnormal ||
        "-",
      "",
    );
    add(
      "Triple Phosphate",
      labData.urinalysistriplephosphateresult ||
        labData.urinalysistriplephosphate ||
        labData.triplephosphateresult,
      labData.urinalysistriplephosphatenormalvalue ||
        labData.urinalysistriplephosphatenormal ||
        "-",
      "",
    );
    add(
      "Calcium phosphate crystal",
      labData.urinalysiscalciumphosphatecrystalresult ||
        labData.urinalysiscalciumphosphatecrystal ||
        labData.calciumphosphatecrystalresult,
      labData.urinalysiscalciumphosphatecrystalnormalvalue ||
        labData.urinalysiscalciumphosphatecrystalnormal ||
        "-",
      "",
    );
    add(
      "Cystine crystal",
      labData.urinalysiscystinecrystalresult ||
        labData.urinalysiscystinecrystal ||
        labData.cystinecrystalresult,
      labData.urinalysiscystinecrystalnormalvalue ||
        labData.urinalysiscystinecrystalnormal ||
        "-",
      "",
    );

    if (derived.length > 0) {
      sources.push(derived);
    }
  };

  push(record?.parameters);
  push(record?.health_data?.parameters);
  push(record?.health_data?.lab_results);
  push(record?.lab_results);
  push(record?.health_data?.health_data?.[0]);
  addDerivedFromLabData(record?.labData);
  addDerivedFromLabData(record?.health_data?.labData);
  addUrineDerivedFromLabData(record?.labData);
  addUrineDerivedFromLabData(record?.health_data?.labData);

  return sources;
};

const mergeParameterCollections = (sources) => {
  const merged = [];
  const seen = new Set();

  for (const source of sources) {
    const normalizedCollection = normalizeParameterCollection(source);
    for (const item of normalizedCollection) {
      const lookupKey = normalizeLookupKey(item?.name);
      if (!lookupKey || seen.has(lookupKey)) {
        continue;
      }

      seen.add(lookupKey);
      merged.push(item);
    }
  }

  return merged;
};

const filterVisibleParameters = (parameters, masterData) => {
  if (!Array.isArray(parameters)) {
    return [];
  }

  return parameters.filter((param) => !shouldHideParameterByMasterData(param, masterData));
};

const getUnifiedParameters = (record, masterData) => {
  const merged = mergeParameterCollections(getCandidateParameterSources(record));
  return filterVisibleParameters(merged, masterData);
};

export {
  normalizeLookupKey,
  normalizeText,
  buildHiddenParameterSet,
  shouldHideParameterByMasterData,
  normalizeParameterItem,
  normalizeParameterCollection,
  getCandidateParameterSources,
  mergeParameterCollections,
  filterVisibleParameters,
  getUnifiedParameters,
};
